import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { computed } from 'vue';

export interface FileDownloadOptions {
  fileName: string;
  data: string | Blob;
  mimeType?: string;
  showShareDialog?: boolean;
}

export function useFileDownload() {
  // Platform detection
  const isNative = computed(() => Capacitor.isNativePlatform());
  const isWeb = computed(() => !Capacitor.isNativePlatform());
  const MAX_NATIVE_FILE_SIZE_BYTES = 15 * 1024 * 1024;

  function normalizeFileName(fileName: string, mimeType?: string): string {
    let normalized = (fileName || '').trim();

    normalized = normalized.replace(/[/\\:*?"<>|]+/g, '-');
    normalized = normalized.replace(/[\r\n\t]+/g, '-');
    normalized = normalized.replace(/\s+/g, ' ');
    normalized = normalized.replace(/[- ]+/g, '-');
    normalized = normalized.replace(/^[-._\s]+|[-._\s]+$/g, '');

    if (!normalized) {
      normalized = 'download';
    }

    if (mimeType === 'application/pdf') {
      normalized = normalized.replace(/\.(pdf)+$/i, '');
      normalized = `${normalized}.pdf`;
    }

    return normalized;
  }

  function getDataSizeInBytes(data: string | Blob): number {
    if (typeof data === 'string') {
      if (data.startsWith('data:')) {
        const base64Part = data.split(',')[1] ?? '';
        return Math.ceil((base64Part.length * 3) / 4);
      }

      return new TextEncoder().encode(data).length;
    }

    return data.size;
  }

  /**
   * Convert various data types to base64 string
   */
  async function convertToBase64(data: string | Blob): Promise<string> {
    if (typeof data === 'string') {
      // If it's already a data URL, extract the base64 part
      if (data.startsWith('data:')) {
        return data.split(',')[1];
      }
      // If it's plain text or other string, convert to base64
      return btoa(unescape(encodeURIComponent(data)));
    }

    // If it's a Blob, convert to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix if present
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(data);
    });
  }

  /**
   * Download file for web platform
   */
  function downloadForWeb(data: string | Blob, fileName: string, mimeType?: string) {
    const normalizedFileName = normalizeFileName(fileName, mimeType);
    let url: string;
    let createdObjectUrl = false;

    if (typeof data === 'string') {
      // Handle string data
      if (data.startsWith('data:')) {
        // Already a data URL
        url = data;
      } else {
        // Convert string to data URL
        const actualMimeType = mimeType || 'text/plain';
        const base64Data = btoa(unescape(encodeURIComponent(data)));
        url = `data:${actualMimeType};base64,${base64Data}`;
      }
    } else {
      // Handle Blob data
      url = URL.createObjectURL(data);
      createdObjectUrl = true;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = normalizedFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL if we created one
    if (createdObjectUrl) {
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }
  }

  /**
   * Download file for native platform
   */
  async function downloadForNative(data: string | Blob, fileName: string, mimeType?: string, showShareDialog = true) {
    try {
      const normalizedFileName = normalizeFileName(fileName, mimeType);
      const dataSizeInBytes = getDataSizeInBytes(data);

      if (dataSizeInBytes > MAX_NATIVE_FILE_SIZE_BYTES) {
        throw new Error(
          `File too large for reliable native export (${Math.round(dataSizeInBytes / (1024 * 1024))}MB). Please reduce file size and try again.`,
        );
      }

      // Convert data to base64
      const base64Data = await convertToBase64(data);

      // Try different directories in order of preference
      // Documents -> Data (app-specific) -> Cache (temporary)
      const directoriesToTry = [
        { dir: Directory.Documents, name: 'Documents' },
        { dir: Directory.Data, name: 'App Data' },
        { dir: Directory.Cache, name: 'Cache' },
      ];

      let result;
      let lastError;

      for (const { dir, name } of directoriesToTry) {
        try {
          console.log(`Attempting to write file to ${name} directory...`);
          result = await Filesystem.writeFile({
            path: normalizedFileName,
            data: base64Data,
            directory: dir,
            recursive: true,
          });
          console.log(`✅ File written successfully to ${name}:`, result);
          break;
        } catch (error) {
          console.warn(`❌ Failed to write to ${name}:`, error);
          lastError = error;

          // If this is a permission error, continue to next directory
          if (error instanceof Error && error.message.includes('EACCES')) {
            console.log(`Permission denied for ${name}, trying next directory...`);
            continue;
          }

          // For other errors, also continue but log differently
          console.log(`Other error in ${name}, trying next directory...`);
          continue;
        }
      }

      // If all directories failed, throw a user-friendly error
      if (!result) {
        const errorMessage = lastError instanceof Error ? lastError.message : 'Unknown error';
        console.error('All file write attempts failed. Last error:', errorMessage);
        throw new Error(`Unable to save file. Please check app permissions or try again. Details: ${errorMessage}`);
      }

      if (showShareDialog) {
        // Share the file using the native share dialog
        const canShare = await Share.canShare();

        if (canShare.value) {
          await Share.share({
            title: normalizedFileName,
            url: result.uri,
            dialogTitle: normalizedFileName,
          });
        }
      }

      return {
        success: true,
        uri: result.uri,
        path: result.uri,
      };
    } catch (error) {
      console.error('Error downloading file on native:', error);
      throw error;
    }
  }

  /**
   * Universal download function that works on both web and native
   */
  async function downloadFile(options: FileDownloadOptions) {
    const { fileName, data, mimeType, showShareDialog = true } = options;
    const normalizedFileName = normalizeFileName(fileName, mimeType);

    try {
      if (isNative.value) {
        return await downloadForNative(data, normalizedFileName, mimeType, showShareDialog);
      } else {
        downloadForWeb(data, normalizedFileName, mimeType);
        return {
          success: true,
          uri: null,
          path: null,
        };
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  /**
   * Share a file that's already been saved (useful for native platforms)
   */
  async function shareFile(filePath: string, fileName: string) {
    if (isNative.value) {
      try {
        await Share.share({
          title: fileName,
          text: `Sharing ${fileName}`,
          url: filePath,
        });
        return { success: true };
      } catch (error) {
        console.error('Error sharing file:', error);
        throw error;
      }
    } else {
      console.warn('Share functionality is not available on web platform');
      return { success: false };
    }
  }

  /**
   * Save file without showing share dialog (useful when you want to save first, then potentially share later)
   */
  async function saveFile(options: Omit<FileDownloadOptions, 'showShareDialog'>) {
    return downloadFile({ ...options, showShareDialog: false });
  }

  return {
    // Platform detection
    isNative,
    isWeb,

    // Main functions
    downloadFile,
    saveFile,
    shareFile,
    normalizeFileName,

    // Utility functions
    convertToBase64,
  };
}
